import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ChevronLeft, RotateCcw, CheckCircle, XCircle, Send, Truck, Clock,
  Package, DollarSign, Building2, FileText, Loader2, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VendorReturnDetail = () => {
  const { returnNumber } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [returnData, setReturnData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const fetchReturn = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/vendor-returns/returns/${returnNumber}`);
      setReturnData(response.data);
    } catch (error) {
      toast.error('Failed to load return details');
      navigate('/vendor-returns');
    } finally {
      setLoading(false);
    }
  }, [returnNumber, navigate]);

  useEffect(() => { fetchReturn(); }, [fetchReturn]);

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/api/vendor-returns/returns/${returnNumber}/approve`, null, {
        params: { approved_by: 'ADMIN' }
      });
      toast.success('Return approved successfully');
      fetchReturn();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handlePost = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/api/vendor-returns/returns/${returnNumber}/post`, null, {
        params: { posted_by: 'ADMIN' }
      });
      toast.success('Return posted - material document created');
      fetchReturn();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) { toast.error('Please enter a cancellation reason'); return; }
    setProcessing(true);
    try {
      await axios.post(`${API}/api/vendor-returns/returns/${returnNumber}/cancel`, null, {
        params: { reason: cancelReason, cancelled_by: 'ADMIN' }
      });
      toast.success('Return cancelled');
      setShowCancelDialog(false);
      fetchReturn();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (val, currency = 'USD') => new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(val || 0);

  const getStatusConfig = (status) => {
    const map = {
      'DRAFT': { color: 'bg-slate-100 text-slate-700', icon: Clock, label: 'Draft' },
      'PENDING_APPROVAL': { color: 'bg-amber-100 text-amber-800', icon: Clock, label: 'Pending Approval' },
      'APPROVED': { color: 'bg-orange-100 text-orange-800', icon: CheckCircle, label: 'Approved' },
      'IN_TRANSIT': { color: 'bg-orange-100 text-orange-800', icon: Truck, label: 'In Transit' },
      'RECEIVED_BY_VENDOR': { color: 'bg-orange-100 text-orange-800', icon: Package, label: 'Received by Vendor' },
      'CREDIT_PENDING': { color: 'bg-orange-100 text-orange-800', icon: DollarSign, label: 'Credit Pending' },
      'COMPLETED': { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle, label: 'Completed' },
      'CANCELLED': { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Cancelled' }
    };
    return map[status] || map['DRAFT'];
  };

  const reasonLabels = {
    'R01': 'Defective Material', 'R02': 'Wrong Material', 'R03': 'Wrong Quantity',
    'R04': 'Damaged in Transit', 'R05': 'Quality Rejection', 'R06': 'Expired Material',
    'R07': 'Order Cancelled', 'R08': 'Duplicate Delivery', 'R09': 'Not as Ordered', 'R99': 'Other'
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-rose-600" />
        </div>
      </Layout>
    );
  }

  if (!returnData) return null;

  const statusConfig = getStatusConfig(returnData.status);
  const StatusIcon = statusConfig.icon;
  const canApprove = returnData.status === 'DRAFT' || returnData.status === 'PENDING_APPROVAL';
  const canPost = returnData.status === 'APPROVED';
  const canCancel = !['COMPLETED', 'CANCELLED'].includes(returnData.status);

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="vendor-return-detail">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/vendor-returns')} data-testid="back-btn">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-600 rounded-lg">
                <RotateCcw className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900" data-testid="return-number">{returnData.return_number}</h1>
                <p className="text-sm text-slate-500">Vendor Return</p>
              </div>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusConfig.color}`} data-testid="return-status">
              <StatusIcon className="h-4 w-4" />
              {statusConfig.label}
            </span>
          </div>
          <div className="flex gap-2">
            {canApprove && (
              <Button onClick={handleApprove} disabled={processing} className="bg-orange-600 hover:bg-orange-700" data-testid="approve-btn">
                <CheckCircle className="h-4 w-4 mr-2" /> Approve
              </Button>
            )}
            {canPost && (
              <Button onClick={handlePost} disabled={processing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="post-btn">
                <Send className="h-4 w-4 mr-2" /> Post
              </Button>
            )}
            {canCancel && (
              <Button variant="outline" onClick={() => setShowCancelDialog(true)} disabled={processing} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="cancel-return-btn">
                <XCircle className="h-4 w-4 mr-2" /> Cancel
              </Button>
            )}
          </div>
        </div>

        {/* Header Info */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4 text-slate-500" /> Vendor & Organization
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-slate-500">Vendor</p><p className="font-medium" data-testid="vendor-id">{returnData.vendor_id}</p>{returnData.vendor_name && <p className="text-xs text-slate-500">{returnData.vendor_name}</p>}</div>
                <div><p className="text-xs text-slate-500">Company Code</p><p className="font-medium">{returnData.company_code}</p></div>
                <div><p className="text-xs text-slate-500">Purch. Org</p><p className="font-medium">{returnData.purchasing_organization}</p></div>
                <div><p className="text-xs text-slate-500">Return Date</p><p className="font-medium">{returnData.return_date}</p></div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-slate-500" /> Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-slate-500">Total Items</p><p className="text-xl font-bold text-slate-900" data-testid="total-items">{returnData.total_items}</p></div>
                <div><p className="text-xs text-slate-500">Total Quantity</p><p className="text-xl font-bold text-slate-900" data-testid="total-qty">{returnData.total_quantity}</p></div>
                <div><p className="text-xs text-slate-500">Total Value</p><p className="text-xl font-bold text-rose-700" data-testid="total-value">{formatCurrency(returnData.total_value, returnData.currency)}</p></div>
                <div>
                  <p className="text-xs text-slate-500">Credit Memo</p>
                  {returnData.credit_memo_number ? (
                    <p className="font-medium text-emerald-600" data-testid="credit-memo">{returnData.credit_memo_number}</p>
                  ) : (
                    <p className="text-sm text-slate-400">-</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Documents */}
        {(returnData.material_document || returnData.shipping_point) && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" /> Related Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                {returnData.material_document && <div><p className="text-xs text-slate-500">Material Document</p><p className="font-medium">{returnData.material_document}</p></div>}
                {returnData.shipping_point && <div><p className="text-xs text-slate-500">Shipping Point</p><p className="font-medium">{returnData.shipping_point}</p></div>}
                {returnData.carrier && <div><p className="text-xs text-slate-500">Carrier</p><p className="font-medium">{returnData.carrier}</p></div>}
                {returnData.tracking_number && <div><p className="text-xs text-slate-500">Tracking #</p><p className="font-medium">{returnData.tracking_number}</p></div>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header Text */}
        {returnData.header_text && (
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-4">
              <p className="text-xs text-slate-500 mb-1">Header Text</p>
              <p className="text-sm text-slate-700">{returnData.header_text}</p>
            </CardContent>
          </Card>
        )}

        {/* Items */}
        <Card className="border-0 shadow-sm overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Package className="h-4 w-4 text-slate-500" /> Return Items
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs">#</TableHead>
                  <TableHead className="text-xs">Material</TableHead>
                  <TableHead className="text-xs">PO / Item</TableHead>
                  <TableHead className="text-xs text-right">Return Qty</TableHead>
                  <TableHead className="text-xs">Reason</TableHead>
                  <TableHead className="text-xs">Plant / SLoc</TableHead>
                  <TableHead className="text-xs text-right">Unit Price</TableHead>
                  <TableHead className="text-xs text-right">Net Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(returnData.items || []).map((item, idx) => (
                  <TableRow key={`${item.po_number || 'po'}-${item.po_item || idx}-${item.item_number || idx}`} data-testid={`item-row-${idx}`}>
                    <TableCell className="font-mono text-xs">{item.item_number}</TableCell>
                    <TableCell>
                      <p className="text-sm font-medium">{item.material_id}</p>
                      <p className="text-xs text-slate-500 truncate max-w-[200px]">{item.material_description}</p>
                    </TableCell>
                    <TableCell className="text-sm">{item.po_number} / {item.po_item}</TableCell>
                    <TableCell className="text-right font-medium">{item.return_quantity} {item.unit_of_measure}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {reasonLabels[item.return_reason] || item.return_reason}
                      </Badge>
                      {item.reason_text && <p className="text-xs text-slate-500 mt-0.5">{item.reason_text}</p>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{item.plant} / {item.storage_location}</TableCell>
                    <TableCell className="text-right text-sm">{formatCurrency(item.unit_price, item.currency)}</TableCell>
                    <TableCell className="text-right text-sm font-medium">{formatCurrency(item.net_value, item.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Audit */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-slate-500">Audit Trail</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-6 text-sm">
              {returnData.created_by && <div><p className="text-xs text-slate-500">Created By</p><p className="font-medium">{returnData.created_by}</p><p className="text-xs text-slate-400">{returnData.created_at}</p></div>}
              {returnData.approved_by && <div><p className="text-xs text-slate-500">Approved By</p><p className="font-medium">{returnData.approved_by}</p><p className="text-xs text-slate-400">{returnData.approved_at}</p></div>}
              {returnData.posted_by && <div><p className="text-xs text-slate-500">Posted By</p><p className="font-medium">{returnData.posted_by}</p><p className="text-xs text-slate-400">{returnData.posted_at}</p></div>}
              {returnData.cancelled_by && <div><p className="text-xs text-red-500">Cancelled By</p><p className="font-medium text-red-600">{returnData.cancelled_by}</p><p className="text-xs text-red-400">{returnData.cancellation_reason}</p></div>}
            </div>
          </CardContent>
        </Card>

        {/* Cancel Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" /> Cancel Vendor Return
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">This will cancel return <strong>{returnData.return_number}</strong>. This action cannot be undone.</p>
              <div className="space-y-2">
                <Label className="text-sm">Cancellation Reason <span className="text-red-500">*</span></Label>
                <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Enter reason for cancellation..." className="bg-slate-50" data-testid="cancel-reason" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCancelDialog(false)}>Keep Return</Button>
              <Button variant="destructive" onClick={handleCancel} disabled={processing} data-testid="confirm-cancel-btn">
                {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Confirm Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default VendorReturnDetail;
